import { DEFAULT_WEATHER_LOCATION, NOTIFIED_KEY, REMINDERS_KEY, STARTER_PLANTS, STORAGE_KEY, THEME_KEY, WEATHER_LOCATION_KEY } from '../constants';
import type { ChloromanceTheme, HydrationState, Plant, WeatherLocation } from '../types';
import { isPlant, normalizePlant } from './plants';

export function getUserStorageKey(baseKey: string, userId: string) {
  return `${baseKey}:${userId}`;
}

export function readPlantsForUser(userId: string): Plant[] {
  try {
    const rawPlants = window.localStorage.getItem(getUserStorageKey(STORAGE_KEY, userId));
    if (!rawPlants) return STARTER_PLANTS;
    const parsedPlants = JSON.parse(rawPlants) as unknown[];
    return Array.isArray(parsedPlants) ? parsedPlants.filter(isPlant).map(normalizePlant) : STARTER_PLANTS;
  } catch {
    return STARTER_PLANTS;
  }
}

export function hasStoredPlantsForUser(userId: string) {
  try {
    return Boolean(window.localStorage.getItem(getUserStorageKey(STORAGE_KEY, userId)));
  } catch {
    return false;
  }
}

export function persistPlants(userId: string, plants: Plant[]) {
  window.localStorage.setItem(getUserStorageKey(STORAGE_KEY, userId), JSON.stringify(plants));
}

export function getStoredWeatherLocation(userId: string): WeatherLocation {
  try {
    const raw = window.localStorage.getItem(getUserStorageKey(WEATHER_LOCATION_KEY, userId));
    if (!raw) return DEFAULT_WEATHER_LOCATION;
    const parsed = JSON.parse(raw) as WeatherLocation;
    return Number.isFinite(Number(parsed.latitude)) && Number.isFinite(Number(parsed.longitude))
      ? parsed
      : DEFAULT_WEATHER_LOCATION;
  } catch {
    return DEFAULT_WEATHER_LOCATION;
  }
}

export function getStoredReminderPreference(userId: string) {
  try {
    return window.localStorage.getItem(getUserStorageKey(REMINDERS_KEY, userId)) === 'enabled';
  } catch {
    return false;
  }
}

export function readNotifiedStates(userId: string): Record<string, HydrationState> {
  try {
    const raw = window.localStorage.getItem(getUserStorageKey(NOTIFIED_KEY, userId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function getInitialChloromanceTheme(userId: string): ChloromanceTheme {
  try {
    const storedTheme = window.localStorage.getItem(getUserStorageKey(THEME_KEY, userId));
    if (storedTheme === 'light' || storedTheme === 'dark') return storedTheme;
  } catch {
    return 'light';
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
